import { defineField, defineType } from "sanity";

const validateTime = (value: string = "") => {
  const timeregex = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeregex.test(value)) {
    return {
      message:
        "Invalid time format, must be in 24h military time format. For example '09:00' or '17:00'"
    };
  }
  return true;
};

export const availabilityTimesObj = defineType({
  type: "object",
  name: "availabilityTimes",
  title: "Availability Times",
  fields: [
    defineField({
      name: "from",
      type: "string",
      title: "From",
      validation: (Rule) => Rule.custom(validateTime)
    }),
    defineField({
      name: "to",
      type: "string",
      title: "To",
      validation: (Rule) => Rule.custom(validateTime)
    })
  ]
});

export const availabilitySpecificDaysOfWeekObj = defineType({
  type: "object",
  name: "availabilitySpecificDaysOfWeek",
  title: "Availability Specific Days of Week",
  fields: [
    defineField({
      name: "monday",
      type: "availabilityTimes",
      title: "Monday",
      description: "Availability on Mondays"
    }),
    defineField({
      name: "tuesday",
      type: "availabilityTimes",
      title: "Tuesday",
      description: "Availability on Tuesdays"
    }),
    defineField({
      name: "wednesday",
      type: "availabilityTimes",
      title: "Wednesday",
      description: "Availability on Wednesdays"
    }),
    defineField({
      name: "thursday",
      type: "availabilityTimes",
      title: "Thursday",
      description: "Availability on Thursdays"
    }),
    defineField({
      name: "friday",
      type: "availabilityTimes",
      title: "Friday",
      description: "Availability on Fridays"
    }),
    defineField({
      name: "saturday",
      type: "availabilityTimes",
      title: "Saturday",
      description: "Availability on Saturdays"
    }),
    defineField({
      name: "sunday",
      type: "availabilityTimes",
      title: "Sunday",
      description: "Availability on Sundays"
    })
  ]
});

const availabilitySpecificDateField = (o: { group?: string } = {}) =>
  defineField({
    name: "availabilitySpecificDate",
    group: o.group,
    type: "array",
    title: "Availability Specific Dates",
    of: [
      {
        type: "object",
        title: "Availability Specific Date",
        fields: [
          defineField({
            name: "date",
            type: "date",
            title: "Date",
            description: "Date of the availability"
          }),
          defineField({
            name: "availabilityTimes",
            type: "availabilityTimes",
            title: "Availability Times"
          })
        ],

        preview: {
          select: {
            date: "date",
            s: "availabilityTimes"
          },
          prepare(selection) {
            const { date, s } = selection as {
              date?: string;
              s?: { from?: string; to?: string };
            };
            return {
              title: "Availability for " + (date ?? ""),
              subtitle: (s?.from ?? "") + " - " + (s?.to ?? "")
            };
          }
        }
      }
    ]
  });

export const supportAvailabilityTypeDocument = defineType({
  type: "document",
  name: "supportAvailability",
  title: "Support Availability",
  groups: [
    {
      title: "General",
      name: "general"
    },
    {
      title: "Specific Payer",
      name: "payer"
    },
    {
      title: "Specific Date",
      name: "date"
    }
  ],
  fields: [
    // general days of the week availability
    defineField({
      name: "availabilityDaysOfWeek",
      type: "availabilitySpecificDaysOfWeek",
      title: "Availability Days of Week",
      group: "general",
      description:
        "All times should be in PST. The time from which the service is available, 24h military time format, for example '09:00' or '17:00'. "
    }),
    // availability for a specific date
    availabilitySpecificDateField({ group: "date" }),

    // availability for a specific payer
    defineField({
      name: "availabilitySpecificPayer",
      group: "payer",
      type: "array",
      title: "Availability Specific Payer",
      of: [
        {
          type: "object",
          title: "Availability Specific Payer",
          fields: [
            defineField({
              name: "payer",
              type: "string",
              title: "Payer",
              description: "Payer of the availability"
            }),
            defineField({
              name: "availabilityDaysOfWeek",
              type: "availabilitySpecificDaysOfWeek",
              title: "Availability Times"
            }),
            availabilitySpecificDateField()
          ]
        }
      ]
    })
  ]
});
